import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall } from "utils/service";

export interface AttachmentConfig {
  allowedTypes: string[];
  maxFileSizeMb: number;
  maxFilesPerSession: number;
}

/**
 * Mirrors the set the sender can upload on step 1 of a request
 * (`UploadDocuments.tsx`'s dropzone) — a signer should not be held to a
 * narrower list than the document itself. One option per format, so `.jpg` and
 * `.jpeg` share an entry (same format, two extensions) while `.doc`/`.docx` and
 * `.xls`/`.xlsx` stay separate.
 */
export const ATTACHMENT_TYPE_OPTIONS: {
  value: string;
  label: string;
  extensions: string[];
}[] = [
  { value: "pdf", label: "PDF", extensions: [".pdf"] },
  { value: "jpg", label: "JPG", extensions: [".jpg", ".jpeg"] },
  { value: "png", label: "PNG", extensions: [".png"] },
  { value: "gif", label: "GIF", extensions: [".gif"] },
  { value: "bmp", label: "BMP", extensions: [".bmp"] },
  { value: "tiff", label: "TIFF", extensions: [".tiff"] },
  { value: "heic", label: "HEIC", extensions: [".heic"] },
  { value: "doc", label: "DOC", extensions: [".doc"] },
  { value: "docx", label: "DOCX", extensions: [".docx"] },
  { value: "txt", label: "TXT", extensions: [".txt"] },
  { value: "xls", label: "XLS", extensions: [".xls"] },
  { value: "xlsx", label: "XLSX", extensions: [".xlsx"] },
];

export const DEFAULT_ATTACHMENT_CONFIG: AttachmentConfig = {
  allowedTypes: ["pdf", "jpg", "png"],
  maxFileSizeMb: 10,
  maxFilesPerSession: 5,
};

export const ATTACHMENT_MAX_FILE_SIZE_LIMIT_MB = 10;
/** The server rejects anything above this with a 400, so the field stops here. */
export const ATTACHMENT_MAX_FILES_LIMIT = 25;

const sanitize = (raw: any): AttachmentConfig => {
  const allowedValues = ATTACHMENT_TYPE_OPTIONS.map((option) => option.value);
  const allowedTypes = Array.isArray(raw?.allowedTypes)
    ? raw.allowedTypes.filter((type: any) => allowedValues.includes(type))
    : [];
  const maxFileSizeMb = Number(raw?.maxFileSizeMb);
  const maxFilesPerSession = Number(raw?.maxFilesPerSession);
  return {
    allowedTypes: allowedTypes.length
      ? allowedTypes
      : DEFAULT_ATTACHMENT_CONFIG.allowedTypes,
    maxFileSizeMb:
      Number.isFinite(maxFileSizeMb) && maxFileSizeMb > 0
        ? maxFileSizeMb
        : DEFAULT_ATTACHMENT_CONFIG.maxFileSizeMb,
    maxFilesPerSession:
      Number.isFinite(maxFilesPerSession) && maxFilesPerSession > 0
        ? maxFilesPerSession
        : DEFAULT_ATTACHMENT_CONFIG.maxFilesPerSession,
  };
};

/** Exposed so the signer side can normalise the policy echoed on the envelope. */
export const sanitizeAttachmentConfig = sanitize;

/** Reads the account's policy. Settings screen only — the signer is a guest and cannot call this. */
export const getAttachmentConfig = async (): Promise<AttachmentConfig> => {
  const callParams = await getCallParams("GET");
  const response = await makeCall(
    urls.GET_ATTACHMENT_CONFIGURATION,
    callParams,
  );
  return sanitize(response);
};

export const updateAttachmentConfig = async (
  config: AttachmentConfig,
): Promise<AttachmentConfig> => {
  const sanitized = sanitize(config);
  const callParams = await getCallParams("POST", {
    value: JSON.stringify(sanitized),
  });
  await makeCall(urls.UPDATE_ATTACHMENT_CONFIGURATION, callParams);
  return sanitized;
};

let pendingAccountConfig: Promise<AttachmentConfig> | null = null;
/** The resolved value of `pendingAccountConfig`, for callers that cannot await. */
let loadedAccountConfig: AttachmentConfig | null = null;

export const loadAccountAttachmentConfig = (): Promise<AttachmentConfig> => {
  if (!pendingAccountConfig) {
    pendingAccountConfig = getAttachmentConfig()
      .then((config) => {
        loadedAccountConfig = config;
        return config;
      })
      .catch((error) => {
        pendingAccountConfig = null;
        throw error;
      });
  }
  return pendingAccountConfig;
};

/**
 * The account policy if `loadAccountAttachmentConfig` has already resolved, else `null`. For
 * synchronous code such as the step validator; it never triggers a fetch, so a `null` means
 * "unknown", not "use the default".
 */
export const getLoadedAccountAttachmentConfig = (): AttachmentConfig | null =>
  loadedAccountConfig;

/** Called after the settings are saved, so the editor picks up the change without a reload. */
export const invalidateAccountAttachmentConfig = () => {
  pendingAccountConfig = null;
  loadedAccountConfig = null;
};

export const toAcceptedExtensions = (allowedTypes: string[]): string[] =>
  ATTACHMENT_TYPE_OPTIONS.filter((option) =>
    allowedTypes.includes(option.value),
  ).flatMap((option) => option.extensions);

export const formatAllowedTypesLabel = (allowedTypes: string[]): string =>
  ATTACHMENT_TYPE_OPTIONS.filter((option) =>
    allowedTypes.includes(option.value),
  )
    .map((option) => option.label)
    .join(", ");
